import moment from 'moment';
import { FaSort } from 'react-icons/fa';
import { formatDurationMs } from './useBannerAnalytics';

// D-08: sortable headers live in this screen's own column definitions —
// the shared table component is not modified. `disableSortBy: true` on
// every column stops react-table from re-sorting the page client-side;
// all sorting is server-side via onSort -> useBannerAnalytics.onSort.
// Near-black like the table's cell text; unsorted icons stay faded grey.
const ACTIVE_SORT_COLOR = '#212529';

// Plain header text like every other table (no `link` class — it underlines
// and adds a side margin that pushes the header off its column), followed by
// one ⇅ icon on every column; only the sorted column's icon is highlighted.
const SortableHeader = ({ columnId, label, sortBy, sortOrder, onSort }) => {
  const isActive = sortBy === columnId;
  return (
    <span
      className="d-inline-flex align-items-center cursor-pointer"
      role="button"
      aria-label={
        isActive
          ? `Sort by ${label} (currently ${sortOrder === 'asc' ? 'ascending' : 'descending'})`
          : `Sort by ${label}`
      }
      onClick={() => onSort(columnId)}
    >
      {label}
      <FaSort
        size={12}
        className="ms-1"
        style={isActive ? { color: ACTIVE_SORT_COLOR } : { opacity: 0.35 }}
        aria-hidden="true"
      />
    </span>
  );
};

// Factory taking the active sort state so headers can render their own
// direction arrow. Explicit ids (name/phone/email/impressionTime/opens/
// totalTime/lastOpened) — none of PaginationTable's special ids (Actions/
// Status/...) — exactly the seven columns in D-09: Name, Phone, Email,
// Banner impressions, Link clicks, Time on link, Last opened. No Mall, no Tier.
export function buildBannerAnalyticsColumns({ sortBy, sortOrder, onSort }) {
  const header = (columnId, label) => () => (
    <SortableHeader
      columnId={columnId}
      label={label}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
    />
  );

  return [
    {
      id: 'name',
      Header: header('name', 'Name'),
      accessor: (row) => row.name,
      Cell: ({ value }) => value || '',
      disableSortBy: true,
    },
    {
      id: 'phone',
      Header: header('phone', 'Phone'),
      accessor: (row) => row.phone,
      Cell: ({ value }) => value || '',
      disableSortBy: true,
    },
    {
      id: 'email',
      Header: header('email', 'Email'),
      accessor: (row) => row.email,
      Cell: ({ value }) => value || '',
      disableSortBy: true,
    },
    {
      id: 'impressionTime',
      Header: header('impressionTime', 'Banner impressions'),
      accessor: (row) => row.impressionTimeMs,
      Cell: ({ value }) => formatDurationMs(value),
      disableSortBy: true,
    },
    {
      id: 'opens',
      Header: header('opens', 'Link clicks'),
      accessor: (row) => row.opens,
      Cell: ({ value }) => value ?? 0,
      disableSortBy: true,
    },
    {
      id: 'totalTime',
      Header: header('totalTime', 'Time on link'),
      accessor: (row) => row.totalTimeMs,
      Cell: ({ value }) => formatDurationMs(value),
      disableSortBy: true,
    },
    {
      id: 'lastOpened',
      Header: header('lastOpened', 'Last opened'),
      accessor: (row) => row.lastOpenedAt,
      Cell: ({ value }) => (value ? moment(value).format('DD-MM-YYYY hh:mm A') : ''),
      disableSortBy: true,
    },
  ];
}
